import React from "react";
import { Helmet } from "react-helmet-async";
import "./AuditManagement.css";
import AuditManagementHeader from "../../assets/images/Blogs/AuditManagement/auditManagement.PNG";
import AuditEventExample from "../../assets/images/Blogs/AuditManagement/extensions.PNG";
import AuditEventMappingExample from "../../assets/images/Blogs/AuditManagement/auditeventmappingexample.PNG";
import AuditDefinationExample from "../../assets/images/Blogs/AuditManagement/AuditDefinationExample.PNG";
import AuditExtentionExample from "../../assets/images/Blogs/AuditManagement/auditextensionexample.PNG";
import AuditPrimaryRecordExample from "../../assets/images/Blogs/AuditManagement/primaryauditexample.PNG";
import AuditSecondaryRecordExample from "../../assets/images/Blogs/AuditManagement/secondaryauditexample.PNG";
import StepAuditLogging from "../../assets/images/Blogs/AuditManagement/stepstosetupauditlogging.PNG";
import Usage from "../../assets/images/Blogs/AuditManagement/UsageExample.PNG";
import PrimaryAndSecondaryExample from "../../assets/images/Blogs/AuditManagement/primaryandsecondaryaudit.PNG";
import SearchAuditRecord from "../../assets/images/Blogs/AuditManagement/SearchAuditRecordDiagram.PNG";
import GenerateAuditReport1 from "../../assets/images/Blogs/AuditManagement/GenerateAuditReportDiagram.PNG";
import GenerateAuditReport2 from "../../assets/images/Blogs/AuditManagement/GenerateAuditReportDiagram2.PNG";
import ExportAuditLogs from "../../assets/images/Blogs/AuditManagement/ExportAuditLogImage.PNG";
import ExportAuditLogs2 from "../../assets/images/Blogs/AuditManagement/ExportAuditLogImage2.PNG";
import UseCase from "../../assets/images/Blogs/AuditManagement/UseCaseDiagram.PNG";
import EventTypeMapping from "../../assets/images/Blogs/AuditManagement/EventTypeMappingDiagram.PNG";
import AuditDefinationEPM from "../../assets/images/Blogs/AuditManagement/AuditDefinationEPMTask.png";

const auditManagementOgImage =
  "https://nextechplm.com/static/media/auditManagement.623011f2086ccbefc638.PNG";
// React Component
const AuditManagement = () => {
  return (
    <>
      <Helmet>
        <title>Audit Management in Teamcenter | NexTech PLM Solutions</title>
        <meta name="description" content="Learn how Teamcenter audit management tracks who changed product data, what changed, and when — with audit events, mappings, definitions, and extensions explained step by step." />
        <meta name="keywords" content="Teamcenter audit management, PLM audit logging, product data audit, Teamcenter change tracking, audit event mapping, audit definition" />
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
        <link rel="canonical" href="https://nextechplm.com/blog/audit-management" />
        <meta property="og:site_name" content="NexTech PLM Solutions" />
        <meta property="og:type" content="article" />
        <meta property="og:title" content="Audit Management in Teamcenter | NexTech PLM Solutions" />
        <meta property="og:description" content="Learn how Teamcenter audit management tracks who changed product data, what changed, and when with audit events, mappings, definitions, and extensions explained step by step." />
        <meta property="og:url" content="https://nextechplm.com/blog/audit-management" />
        <meta property="og:image" content={auditManagementOgImage} />
        <meta property="og:image:secure_url" content={auditManagementOgImage} />
        <meta property="og:image:alt" content="Audit management Teamcenter screen preview" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Audit Management in Teamcenter | NexTech PLM Solutions" />
        <meta name="twitter:description" content="Learn how Teamcenter audit management tracks who changed product data, what changed, and when with audit events, mappings, definitions, and extensions explained step by step." />
        <meta name="twitter:image" content={auditManagementOgImage} />
      </Helmet>
      <section className="banner-audit">
        <h1 className="banner-title">Audit Management</h1>
      </section>

      <section className="container content-container">
        <div className="text-container">
          <p className="text-bold">
            Teamcenter has transformed the way data is tracked in the PLM
            environment: Who caused it? What changed? And when? As PLM expands
            its scope across the extended enterprise, the need for not only
            securing data but also tracking changes becomes increasingly
            important.
          </p>
          <p>
            To maintain control over your valuable product knowledge, you need
            to know what information is being changed, by whom, and sometimes
            who is accessing it.
          </p>
        </div>
        <div className="image-container">
          <img src={AuditManagementHeader} alt="Audit Management" />
        </div>
      </section>

      <section className="container audit-log-section">
        <div className="text-container">
          <h2 className="sub-title">What is an Audit Log?</h2>
          <p>
            An audit log, or audit data, refers to any recorded information
            about activities for future reference.
          </p>
          <p>It involves capturing details such as:</p>
          <ul className="audit-log-list">
            <li>
              <b>Who</b> performed the action (user)
            </li>
            <li>
              <b>What</b> action was taken (e.g., modify, delete,
              check-in/check-out, read)
            </li>
            <li>
              <b>Which</b> object was affected (e.g., Item, Item Revision,
              Dataset, Form)
            </li>
            <li>
              <b>When</b> the action occurred (timestamp)
            </li>
            <li>
              <b>What</b> information was changed as a result of the action
            </li>
          </ul>
        </div>
      </section>

      <section className="container">
        <h2 className="sub-title">What is Teamcenter Audit Management?</h2>
        <p>
          Teamcenter audit management enables you to track activities happening
          on data in your PLM environment and retain it as an audit log, helping
          to:
        </p>
        <ul className="audit-management-list">
          <li>Identify what information has changed</li>
          <li>Identify which specific properties have changed</li>
          <li>See who changed it</li>
          <li>See what action triggered the change</li>
          <li>Compare the old and new values of the changed data</li>
          <div className="example-container">
            <h5 className="example-title">
              <b>Example:</b>
            </h5>
            <p>
              Track who changed the value of a specific property and what the
              old value was (e.g., tracking changes to a cost field).
            </p>
          </div>
        </ul>
      </section>

      <section className="container">
        <h2 className="sub-title">Audit Logging Key Elements</h2>
        <div className="content-container">
          <div className="text-container">
            <h3 className="key-element-title">Audit Event</h3>
            <p>
              An event is an action that occurs to workspace objects such as
              Item, Item Revisions, Datasets, Forms, as well as administration
              objects like Users, Groups, Roles, Projects, ADA licenses in
              Teamcenter.
            </p>
            <div className="example-container">
              <h5 className="example-title">
                <b>Example:</b>
              </h5>
              <p>
                Check-in, Check-out, Edit, Delete, File Read, File Write,
                Add/Remove user to project.
              </p>
            </div>
          </div>
          <div className="image-container">
            <img src={AuditEventExample} alt="Audit Event Example" />
          </div>
        </div>

        <div className="content-container">
          <div className="text-container">
            <h3 className="key-element-title">Audit Event Mapping</h3>
            <p>
              Event mapping is a process of connecting or linking specific event
              to a business object type.
            </p>
            <div className="example-container">
              <h5 className="example-title">
                <b>Example:</b>
              </h5>
              <p>
                Checkin, Checkout, Delete, Modify events are mapped with Item as
                shown below
              </p>
            </div>
            <p>
              (To make available secondary audit properties while defining audit
              defination you have to select secondary audit type field here){" "}
            </p>
          </div>
          <div className="image-container">
            <img
              src={AuditEventMappingExample}
              alt="Audit Event Mapping Example"
            />
          </div>
        </div>

        <div className="content-container">
          <div className="text-container">
            <h3 className="key-element-title">Audit Defination</h3>
            <p>
              Audit definition is to define what information to capture about a
              particular object when an event occurs.
            </p>
            <div className="example-container">
              <h5 className="example-title">
                <b>Example:</b>
              </h5>
              <p>
                Capture object_name, check_out_date, checked_out_user,
                gov_classification on happening modify event on item as shown
                below
              </p>
            </div>
            <p>
              (You can also capture secondary object information related to
              primary object on happening event on primary object){" "}
            </p>
          </div>
          <div className="image-container">
            <img src={AuditDefinationExample} alt="Audit Defination Example" />
          </div>
        </div>

        <div className="content-container">
          <div className="text-container">
            <h3 className="key-element-title">Audit Extention</h3>
            <p>
              A log extension allows you to write additional data, other than
              the data captured by audit logs.
            </p>
            <div className="example-container">
              <h5 className="example-title">
                <b>Example:</b>
              </h5>
              <p>
                Fnd0CICO_auditloghandler to capture additional information such
                as change ID, and the reason to audit on checkin and checkout
                events.
              </p>
            </div>
            <p>
              OOTB Teamcenter has provided several extensions which you can use
              to collect additional data. You can also create custom extension
              based on your requirement to capture additional info in audit log.
            </p>
          </div>
          <div className="image-container">
            <img src={AuditExtentionExample} alt="Audit Defination Example" />
          </div>
        </div>

        <div className="content-container">
          <div className="text-container">
            <h3 className="key-element-title">Primary Audit Record</h3>
            <p>
              Primary logs holds the information about an object on which the
              action is happened. This you have to define using audit defination
              as shown below.
            </p>
            <div className="example-container">
              <h5 className="example-title">
                <b>Example:</b>
              </h5>
              <p>
                Collect information about EPMTask object on action Assign
                Approver as shown in the snap below
              </p>
            </div>
          </div>
          <div className="image-container">
            <img
              src={AuditPrimaryRecordExample}
              alt="Audit primary record Example"
            />
          </div>
        </div>

        <div className="content-container">
          <div className="text-container">
            <h3 className="key-element-title">Secondary Audit Log</h3>
            <p>
              Secondary logs are required when additional information is needed
              on a related object. This you have to define using audit
              defination as shown below.
            </p>
            <div className="example-container">
              <h5 className="example-title">
                <b>Example:</b>
              </h5>
              <p>
                EPMTask (the primary object) is related to a workspace object
                (the secondary object say Item Revision, and properties of the
                workspace object are required. )
              </p>
            </div>

            <p>
              Similarly, an ADA License (the Primary object) is attached to a
              workspace object (the secondary object), and properties of the
              workspace object are required.
            </p>
          </div>
          <div className="image-container">
            <img
              src={AuditSecondaryRecordExample}
              alt="Audit secondary record Example"
            />
          </div>
        </div>
      </section>

      <section className="container audit-log-section">
        <div className="text-container">
          <h2 className="sub-title">Steps to setup Audit Loging</h2>
          <div className="image-container">
            <img src={StepAuditLogging} alt="Audit secondary record Example" />
          </div>
          <h3 className="key-element-title">Usage</h3>
          <div class="alert alert-primary" role="alert">
            <b>Note : </b> Logs are logically grouped as shown below, this helps
            you to quickly browse through required log
          </div>
          <div className="image-container">
            <img src={Usage} alt="Audit secondary record Example" />
          </div>
          <div className="content-container">
            <div className="text-container">
              <h3 className="key-element-title">
                View Primary and Secondary Audit Log{" "}
              </h3>
              <p>
                Use summary stylesheet to view Audit records in Summary tab of
                each object in Rich client
              </p>
            </div>
            <div className="image-container">
              <img
                src={PrimaryAndSecondaryExample}
                alt="Audit secondary record Example"
              />
            </div>
          </div>
          <div className="content-container">
            <div className="text-container">
              <h3 className="key-element-title">Search Audit Record</h3>
              <p>Use queries to search audit record</p>
            </div>

            <img src={SearchAuditRecord} alt="Audit secondary record Example" />
          </div>
          <div className="content-container">
            <div className="text-container">
              <h3 className="key-element-title">Search Audit Record</h3>
              <p>Use queries to search audit record</p>
            </div>

            <img src={SearchAuditRecord} alt="Audit secondary record Example" />
          </div>{" "}
          <div className="content-container">
            <div className="text-container">
              <h3 className="key-element-title">Genarate Audit Report </h3>
              <p>
                Various Audit reports can be generated using Report Application
                or Teamcenter Reporting and Analytics
              </p>
            </div>
            <div className="image-container">
              <img
                src={GenerateAuditReport1}
                alt="Audit secondary record Example"
              />
            </div>
            <img
              src={GenerateAuditReport2}
              alt="Audit secondary record Example"
            />
          </div>

          <div className="text-container">
            <h3 className="key-element-title">Export audit logs  </h3>
            <p>
            You can export the audit logs to CSV or XLS file 
            </p>

            <img src={ExportAuditLogs} alt="Export Audit Logs" />
            <img style={{marginTop:"50px"}} src={ExportAuditLogs2} alt="Export Audit Logs" />
           </div>


           <div className="logs-container">
      <h2>Common Use Cases</h2>
      <p>There are various use cases where you need to track the information.</p>

      <h3>General Logs:</h3>
      <p>Record details on below actions:</p>
      <ul>
        <li>Check-Out</li>
        <li>Check-In</li>
        <li>Modify</li>
        <li>Delete</li>
      </ul>
      <p>To support:</p>
      <ul>
        <li>Troubleshooting access issues</li>
        <li>Audit object change history</li>
        <li>Analyze who has changed what on these actions</li>
      </ul>

      <h3>File Access Logs:</h3>
      <p>Record details on:</p>
      <ul>
        <li>File read action</li>
        <li>File modify action</li>
      </ul>
      <p>To support:</p>
      <ul>
        <li>Analysis of file usage</li>
        <li>Addressing file access related security issues</li>
        <li>Audit dataset change history</li>
        <li>Tracing file changes over a period</li>
      </ul>

      <h3>Workflow Logs:</h3>
      <p>Record details like:</p>
      <ul>
        <li>Dates/time for important workflow steps</li>
        <li>Review initiator's profile</li>
        <li>Micro management steps</li>
      </ul>
      <p>To:</p>
      <ul>
        <li>Support decisions like postponing or advancing review meetings</li>
        <li>Adjust budget for task completion</li>
        <li>Resource allocation/release</li>
      </ul>

      <img src={UseCase} alt="usecase diagram" />
    </div>

    <div className="logs-section">
      <h3>ADA License Logs:</h3>
      <p>Record details like:</p>
      <ul>
        <li>Who has attached ADA license to which business object and when</li>
        <li>
          Capture secondary object (to which the license got attached) details such as GOV classification, name, description, owner, and other properties
        </li>
      </ul>
      <p>To:</p>
      <ul>
        <li>Understand data export controls trend in the system</li>
        <li>Keep an eye on sharing data through export control</li>
      </ul>

      <h3>Project Logs:</h3>
      <p>Record details like:</p>
      <ul>
        <li>Project management tasks: who created, modified, deleted project along with properties</li>
        <li>
          Project team management tasks: which user got added through user/role/group similar privileges/non-privileges/team admin, default project set, and so on
        </li>
      </ul>
      <p>To:</p>
      <ul>
        <li>Trace various activities performed in the context of the project</li>
        <li>Keep a record of the project team for future audit</li>
      </ul>
    </div>


    <div className="installation-config">
      <h2>Installation and Configuration Involved:</h2>
      
      <h3>Installation/License:</h3>
      <p>
        Audit manager is a part of Teamcenter foundation and hence doesn’t require any explicit installation as well as license.
      </p>
      
      <h3>Configuration:</h3>
      <h4>Preferences:</h4>
      <p>
        To enable audit manager, you have to set the following preferences:
      </p>
      <pre>
        TC_audit_manager = ON<br />
        TC_audit_manager_version = 3.
      </pre>
      <p>
        (There are some preferences to help you control the number of audit records in the table, access control, and so on.)
      </p>
      
      <h3>Access on Audit Record:</h3>
      <p>
        Using access manager, you can define access control on audit log. By default, users can view audit logs only if they have read access to the relevant object.
      </p>
      <p>
        To honor AM-based access rules, you have to set:
      </p>
      <pre>TC_audit_avoid_delegation_for_audit_access</pre>
      
      <h4>Example:</h4>
      <ul>
        <li>Limit audit record access to admin only for the deleted object.</li>
        <li>Limit workflow audit record to specific people who are involved in workflow.</li>
      </ul>
      
      <h3>BMIDE:</h3>
      <p>
        Audit logs are stored in the database. Audit log objects store the respective audit logs.
      </p>
      <p>Example: <code>Fnd0FileAccessAudit</code> stores file access audit logs.</p>
      <p>Audit logs type to be used in event type mapping.</p>

      <img src={EventTypeMapping} alt="Event Type Mapping" />
      <p>You can create audit configuration objects such as audit definition, events, and event type in using BMIDE (refer the details given in section “Audit Key Elements”) </p>
    </div>

    <div className="points-to-remember">
      <h2>Points to Remember:</h2>
      
      <ul>
        <li>
          <strong>OOTB</strong> there are some audit logs enabled in the system. Based on your requirement, you have to turn on the “is Active Flag” on the required audit definition or build your own definition.
        </li>
        <img src={AuditDefinationEPM} alt="Audit Defination EPM Task" />
        <li>
          <strong>Siemens recommends</strong> enabling auditing only for required actions and that too for specific objects of your business interest, not for all, to keep the audit table size low in the database.
        </li>
        <li>
          Define a <strong>purging cycle</strong> based on the volume of audit data generated in the system to keep the database clean.
        </li>
      </ul>
    </div>


    <div className="benefits-section">
      <h2>Benefits:</h2>
      <p>
        I can summarize a few benefits of <strong>“Teamcenter Audit Management”</strong> as below:
      </p>
      <ul>
        <li>
          <strong>Detailed Insights:</strong> Audit logs give you insights on the usage of the system in the context of important documents or processes, helping increase efficiency and security in a reliable, provable way.
        </li>
        <li>
          <strong>Demonstrate Compliance:</strong> Audit logs can be used as proof of regulatory compliance during an audit and can help your company fulfill its record-keeping requirements for compliance purposes.
        </li>
        <li>
          <strong>Increased Security:</strong> Having audit logs can protect your business from liability during legal battles. They also help monitor data for any potential security breaches or internal misuse of information.
        </li>
      </ul>
    </div>
        </div>
      </section>
    </>
  );
};

export default AuditManagement;
